import { requireService, wrap } from '@repo/di-ez';
import { AddRoundLight, CloseRoundLight, DoneRoundLight } from '@repo/go-icons';
import { Button, Drawer, Loader } from '@repo/go-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TrajectoryDetails } from './trajectory-details';
import { TrajectoryListItem } from './trajectory-list-item';
import styles from './trajectory-list-sidebar.module.scss';
import { TrajectoryListSidebarStore } from './trajectory-list-sidebar.store';

type Props = {
  isEditing?: boolean;
};

export const TrajectoryListSidebar = wrap<Props>(function TrajectoryListSidebar({ isEditing = false }) {
  const { t } = useTranslation();
  const uiStore = requireService('trajectoryUiStore');
  const trajectoryService = requireService('trajectoryService');
  const [store] = useState(() => new TrajectoryListSidebarStore(trajectoryService, uiStore));

  useEffect(() => store.init(), [store]);

  const preview = store.previewTrajectory;
  const parentTitle = preview?.parentTrajectoryId
    ? store.trajectories.find((item) => item.id === preview.parentTrajectoryId)?.citation.title
    : null;

  return (
    <Drawer
      open={uiStore.isTrajectoryListOpen}
      onClose={store.close}
      style={{ width: 590 }}
      title={t('ListSidebar.title')}
      container={uiStore.settingsDrawerContainer ?? undefined}
      className={styles.drawer}
    >
      <div className={styles.body}>
        <div className={styles.listPane}>
          <div className={styles.list}>
            {store.isLoading && store.trajectories.length === 0 ? (
              <div className={styles.loader}>
                <Loader />
              </div>
            ) : (
              store.trajectories.map((trajectory) => (
                <TrajectoryListItem
                  key={trajectory.id}
                  trajectory={trajectory}
                  selected={store.previewTrajectoryId === trajectory.id}
                  onSelect={store.setPreviewTrajectoryId}
                />
              ))
            )}
          </div>
          <div className={styles.listFooter}>
            <Button className={styles.addButton} size="s" icon={<AddRoundLight />} disabled>
              {t('ListSidebar.addVersion')}
            </Button>
          </div>
        </div>

        <div className={styles.detailsPane}>
          <div className={styles.detailsScroll}>
            <TrajectoryDetails trajectory={preview} parentTitle={parentTitle} />
          </div>
          {isEditing && (
            <div className={styles.footer}>
              <Button
                className={styles.footerButton}
                size="s"
                variant="danger"
                icon={<CloseRoundLight />}
                onClick={store.close}
              >
                {t('ListSidebar.cancel')}
              </Button>
              <Button
                className={styles.footerButton}
                size="s"
                variant="success"
                icon={<DoneRoundLight />}
                onClick={store.apply}
              >
                {t('ListSidebar.apply')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
});
